import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import CustomButton from "../pages/CustomButton";
import Loader from "../dist/Loader";
import formatDate from "../utils/formatDate";
import { toast } from "react-toastify";
import XLSX from "xlsx-js-style";

/* =========================
   MAIN COMPONENT
========================= */
export default function DealDelightFFCount() {
  const [states, setStates] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPobModal, setShowPobModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [giftName, setGiftName] = useState("DD Count");
  const printRef = useRef();
  /* ===== MONTH / YEAR ===== */
  const now = new Date();
  const currentMonth = now.toLocaleString("default", { month: "long" });
  const currentYear = now.getFullYear();

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  /* ===== FILTERS ===== */
  const [filters, setFilters] = useState({
    state_id: "",
    division_id: "",
    employee_id: "",
    month: currentMonth,
    year: currentYear,
    is_monthly: "",
    // start_pob: 0,
    // start_multiply_by: 0,
    // slab_pob: 0,
    // slab_multiply_by: 0,
  });

  /* =========================
     INITIAL LOAD
  ========================= */
  useEffect(() => {
    fetchStates();
    fetchDivisions();
  }, []);

  /* =========================
     MASTER APIs
  ========================= */
  const openPobModal = (row) => {
    setSelectedRow(row);
    setShowPobModal(true);
  };

  const closePobModal = () => {
    setShowPobModal(false);
    setSelectedRow(null);
  };

  const fetchStates = async () => {
    const res = await axios.get(`${BASE_URL}/api/states`, {
      headers: getAuthHeaders(),
    });
    setStates(res.data || []);
  };

  const fetchDivisions = async () => {
    const res = await axios.get(`${BASE_URL}/api/divisions`, {
      headers: getAuthHeaders(),
    });

    const list = res.data || [];
    setDivisions(list);

    // ✅ DEFAULT DIVISION AUTO SELECT
    if (list.length > 0) {
      setFilters((prev) => ({
        ...prev,
        division_id: list[0].id,
      }));
    }
  };



  const fetchEmployees = async (stateId, divisionId) => {
    if (!divisionId) {
      setEmployees([]);
      return;
    }

    const res = await axios.get(`${BASE_URL}/api/employees`, {
      headers: getAuthHeaders(),
      params: {
        ...(stateId && { state_id: stateId }),
        division_id: divisionId,
      },
    });

    setEmployees(res.data || []);
  };

  const handlePrint = () => {
    if (!printRef.current) return;

    const printWindow = window.open("", "_blank", "width=1200,height=800");
    if (!printWindow) {
      toast.error("Pop-up blocked. Please allow pop-ups.");
      return;
    }

    const doc = printWindow.document;
    doc.open();

    doc.write(`
    <!doctype html>
    <html>
      <head>
        <title>Deal Delight Report</title>
        <style>
          @page {
            size: landscape;
            margin: 10mm;
          }

          body {
            font-family: Arial, sans-serif;
            padding: 12px;
            color: #111;
          }

          h2 {
            text-align: center;
            margin-bottom: 10px;
          }

          table {
            width: 100%;
            border-collapse: collapse;
            font-size: 11px;
          }

          th, td {
            border: 1px solid #000;
            padding: 4px;
            white-space: nowrap;
          }

          th {
            background: #f3f4f6;
            font-weight: bold;
          }

          thead {
            display: table-header-group;
          }

          tr {
            page-break-inside: avoid;
          }

          button {
            display: none !important;
          }
        </style>
      </head>
      <body>
      </body>
    </html>
  `);

    const clone = printRef.current.cloneNode(true);

    // remove any buttons inside table (POB clickable etc.)
    clone.querySelectorAll("button").forEach((b) => b.remove());

    doc.body.appendChild(clone);
    doc.close();

    printWindow.focus();
    setTimeout(() => printWindow.print(), 300);
  };

  const handleExportExcel = () => {
    if (!records.length) return;

    // =====================================
    // HELPER
    // =====================================

    const fixValue = (val) => {
      const raw = String(val ?? "").trim();
      return /^\d+$/.test(raw) ? Number(raw) : raw;
    };

    // =====================================
    // BUILD ROWS
    // =====================================

    const excelData = records.map((r, i) => ({
      "S.No": i + 1,
      "Employee ID": fixValue(r.employee),
      "Employee Name": r.employee_name,
      State: r.state_name,
      "Sub State": r.substate,
      "Retailer Type": r.retailer_type,
      "Retailer Code": r.retailer_id,
      "Retailer Name": r.retailer_name,
      City: r.retailer_city,
      Area: r.retailer_area,
      Route: r.retailer_route,
      Mobile: r.retailer_mobile || r.retailer_phone,
      "System Added Date": r.retailer_created_at,
      "Stockist Code": fixValue(r.stockist_code),
      "Stockist Name": r.stockist_name,
      "DD Count": Number(r.total_gift || 0),
      POB: Number(r.total_pob || 0),
    }));

    // =====================================
    // TOTAL ROW
    // =====================================

    excelData.push({
      "S.No": "",
      "Employee ID": "",
      "Employee Name": "",
      State: "",
      "Sub State": "",
      "Retailer Type": "",
      "Retailer Code": "",
      "Retailer Name": "",
      City: "",
      Area: "",
      Route: "",
      Mobile: "",
      "System Added Date": "",
      "Stockist Code": "",
      "Stockist Name": "TOTAL",
      "DD Count": records.reduce((sum, r) => sum + Number(r.total_gift || 0), 0),
      POB: records.reduce((sum, r) => sum + Number(r.total_pob || 0), 0),
    });

    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // =====================================
    // FORCE TYPES (Employee ID + Stockist Code)
    // =====================================

    const range = XLSX.utils.decode_range(worksheet["!ref"]);
    const empColIndex = Object.keys(excelData[0]).indexOf("Employee ID");
    const stockistColIndex = Object.keys(excelData[0]).indexOf("Stockist Code");

    for (let R = 1; R <= range.e.r; ++R) {
      let cell = worksheet[XLSX.utils.encode_cell({ r: R, c: empColIndex })];
      if (cell) {
        const raw = String(cell.v ?? "").trim();
        cell.t = /^\d+$/.test(raw) ? "n" : "s";
      }

      cell = worksheet[XLSX.utils.encode_cell({ r: R, c: stockistColIndex })];
      if (cell) {
        const raw = String(cell.v ?? "").trim();
        cell.t = /^\d+$/.test(raw) ? "n" : "s";
      }
    }

    // =====================================
    // STYLES
    // =====================================

    const headerStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" }, sz: 11 },
      fill: { fgColor: { rgb: "2563EB" } },
      alignment: { horizontal: "center", vertical: "center", wrapText: true },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } },
      },
    };

    const rowStyle = {
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } },
      },
    };

    const totalRowStyle = {
      font: { bold: true },
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } },
      },
    };

    // =====================================
    // APPLY STYLES TO ALL CELLS
    // =====================================

    for (let R = range.s.r; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });

        if (!worksheet[cellAddress]) {
          worksheet[cellAddress] = { t: "z", v: "" };
        }

        if (R === 0) {
          worksheet[cellAddress].s = headerStyle;       // Header row
        } else if (R === range.e.r) {
          worksheet[cellAddress].s = totalRowStyle;     // Total row
        } else {
          worksheet[cellAddress].s = rowStyle;          // Data rows
        }
      }
    }

    // =====================================
    // COLUMN WIDTHS
    // =====================================

    worksheet["!cols"] = [
      { wch: 8 },  // S.No
      { wch: 14 },  // Employee ID
      { wch: 28 },  // Employee Name
      { wch: 15 },  // State
      { wch: 15 },  // Sub State
      { wch: 16 },  // Retailer Type
      { wch: 16 },  // Retailer Code
      { wch: 28 },  // Retailer Name
      { wch: 15 },  // City
      { wch: 15 },  // Area
      { wch: 15 },  // Route
      { wch: 16 },  // Mobile
      { wch: 20 },  // System Added Date
      { wch: 16 },  // Stockist Code
      { wch: 28 },  // Stockist Name
      { wch: 12 },  // DD Count
      { wch: 12 },  // POB
    ];

    // =====================================
    // FREEZE HEADER
    // =====================================

    worksheet["!freeze"] = { xSplit: 0, ySplit: 1 };

    // =====================================
    // FILTERS
    // =====================================

    worksheet["!autofilter"] = {
      ref: `A1:${XLSX.utils.encode_col(range.e.c)}1`,
    };

    // =====================================
    // EXPORT
    // =====================================

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, `${filters.month}-${filters.year}`);

    XLSX.writeFile(workbook, `Deal_Delight_${filters.month}_${filters.year}.xlsx`);
  };

  /* =========================
     FILTER CHANGE
  ========================= */
  const handleChange = (key, value) => {
    const updated = { ...filters, [key]: value };
    setFilters(updated);

    if (key === "state_id" || key === "division_id") {
      fetchEmployees(updated.state_id, updated.division_id);
    }
  };

  /* =========================
     GO BUTTON – MAIN API
  ========================= */
  const handleGo = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${BASE_URL}/api/reports/deal-delight-monthly`,
        {
          headers: getAuthHeaders(),
          params: {
            ...(filters.state_id && { state_id: filters.state_id }),
            division_id: filters.division_id,
            ...(filters.employee_id && { employee_id: filters.employee_id }),
            ...(filters.is_monthly !== "" && {
              is_monthly: filters.is_monthly === "true",
            }),
            month_year: `${filters.month} ${filters.year}`,
            start_pob: filters.start_pob,
            slab_pob: filters.slab_pob,
            start_multiply_by: filters.start_multiply_by,
            slab_multiply_by: filters.slab_multiply_by,
          },
        },
      );
      setGiftName(res.data?.gift_name || "DD Count");

      setRecords(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      console.error(err);
      setRecords([]);

      const message =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Something went wrong";

      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-2">
      <EmployeeFilter
        states={states}
        divisions={divisions}
        employees={employees}
        filters={filters}
        months={months}
        years={years}
        onChange={handleChange}
        onGo={handleGo}
        loading={loading}
        setLoading={setLoading}
      />

      {records.length > 0 && (
        <div className="mt-4">
          <POBTable
            records={records}
            onPobClick={openPobModal}
            title={`${giftName} – ${filters.month} ${filters.year}`}
            printRef={printRef}
          />
          {/* Action Buttons */}
          <div className="flex justify-center gap-3 mt-2">
            <CustomButton
              className="whitespace-nowrap"
              variant="print"
              label="Print"
              onClick={handlePrint}
            >
              🖨
            </CustomButton>

            <CustomButton
              className="whitespace-nowrap"
              variant="export"
              label="Export"
              onClick={handleExportExcel}
            >
              📥
            </CustomButton>
          </div>
        </div>
      )}

      {showPobModal && selectedRow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white w-full max-w-4xl rounded-lg shadow-lg">
            {/* Header */}
            <div className="flex justify-between items-center border-b px-4 py-3">
              <h2 className="text-lg font-semibold">
                POB Details – {selectedRow.retailer_name}
              </h2>
              <button
                onClick={closePobModal}
                className="text-xl text-gray-600 hover:text-red-600"
              >
                ✕
              </button>
            </div>

            {/* Table */}
            <div className="p-4 overflow-x-auto">
              <table className="unlock-dcr-leave-table text-sm ">
                <thead className="bg-blue-500 text-white">
                  <tr>
                    <th className="border px-2 py-1 text-center">S.No</th>
                    <th className="border px-2 py-1 text-center">
                      Retailer Name
                    </th>
                    <th className="border px-2 py-1 text-right">POB</th>
                    <th className="border px-2 py-1 text-center">
                      Working Date
                    </th>
                    <th className="border px-2 py-1 text-center">DD Count</th>
                  </tr>
                </thead>

                <tbody>
                  {Array.isArray(selectedRow.day_wise) &&
                    selectedRow.day_wise.length > 0 ? (
                    selectedRow.day_wise.map((d, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="border px-2 py-1 text-center">
                          {i + 1}
                        </td>

                        <td className="border px-2 py-1 text-center">
                          {selectedRow.retailer_name}
                        </td>

                        <td className="border px-2 py-1 text-right font-semibold">
                          {Number(d.pob || 0).toLocaleString()}
                        </td>

                        <td className="border px-2 py-1 text-center">
                          {formatDate(
                            new Date(d.date).toLocaleDateString("en-GB"),
                          )}
                        </td>

                        <td className="border px-2 py-1 text-center">
                          {d.gift || 0}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="5"
                        className="border py-4 text-center text-gray-500"
                      >
                        No POB details available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* =========================
   FILTER COMPONENT
========================= */
function EmployeeFilter({
  states,
  divisions,
  employees,
  filters,
  months,
  years,
  onChange,
  onGo,
  loading,
  setLoading,
}) {
  return (
    <div className="bg-white border-2 border-gray-600">
      {loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/25">
          <Loader onCancel={() => setLoading(false)} />
        </div>
      )}
      <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-4">
        <span class="text-blue-700"> RTR Wise </span>
        <span class="text-blue-500"> Deal Delight </span>
      </h1>

      <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
        <Select
          label="State"
          value={filters.state_id}
          onChange={(v) => onChange("state_id", v)}
        >
          <option value="">Select State</option>
          {states.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>

        <Select
          label="Division"
          value={filters.division_id}
          onChange={(v) => onChange("division_id", v)}
        >
          {divisions.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </Select>

        <div className="grid grid-cols-2 gap-6 md:col-span-2">
          <Select
            label="Employee"
            value={filters.employee_id}
            onChange={(v) => onChange("employee_id", v)}
          >
            <option value="">All</option>
            {employees.map((e) => (
              <option key={e.id} value={e.id}>
                {e.first_name} {e.last_name} {e.employee} | {e.designation || "-"} |{" "}
                {e.division || "-"} | {e.state_name || "-"}
              </option>
            ))}
          </Select>

          <div className="grid grid-cols-[140px_1fr_1fr] items-center gap-2">
            <label>Month</label>
            <select
              className="border px-2 py-1"
              value={filters.month}
              onChange={(e) => onChange("month", e.target.value)}
            >
              {months.map((m) => (
                <option key={m}>{m}</option>
              ))}
            </select>
            <select
              className="border px-2 py-1"
              value={filters.year}
              onChange={(e) => onChange("year", e.target.value)}
            >
              {years.map((y) => (
                <option key={y}>{y}</option>
              ))}
            </select>
          </div>
          {/* Scheme Type */}
          <Select
            label="Scheme Type"
            value={filters.is_monthly}
            onChange={(v) => onChange("is_monthly", v)}
          >
            <option value="false">Day-wise Scheme</option>
            <option value="true">Monthly Total Scheme</option>
          </Select>
        </div>
      </div>

      <div className="flex justify-center py-4">
        <CustomButton
          onClick={onGo}
          disabled={loading || !filters.state_id}
          variant="go"
        >
          {loading ? "Loading..." : "GO"}
        </CustomButton>
      </div>
    </div>
  );
}

/* =========================
   TABLE COMPONENT (UNCHANGED)
========================= */
function POBTable({ records, onPobClick, title, printRef }) {
  const rows = Array.isArray(records) ? records : [];
  if (!rows.length) return null;

  const safe = (v) => (v === null || v === undefined || v === "" ? "-" : v);

  const formatDate = (d) => {
    if (!d) return "-";
    return new Date(d).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };


  return (
    <div className="border rounded shadow  table-wrapper mt-5" ref={printRef}>
      {title && (
        <div className="px-3 py-2 border-b bg-white">
          <h2 className="text-center font-semibold text-gray-800">{title}</h2>
        </div>
      )}
      <table className="unlock-dcr-leave-table text-sm ">
        <div></div>
        <thead className="bg-blue-500 text-white">
          <tr>
            {[
              "S.No",
              "Emp ID",
              "Employee Name",
              "State",
              "Sub State",
              "Type",
              "Code",
              "Retailer",
              "City",
              "Area",
              "Route",
              "Mobile",
              "System Added Date",
              "Stk Code",
              "Stockist Name",
              "DD Count",
              "POB",
            ].map((h) => (
              <th
                key={h}
                className="border px-2 py-1 text-center whitespace-nowrap"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="bg-white even:bg-gray-50">
              <td className="border px-2 text-center">{i + 1}</td>

              <td className="border px-2 text-center">{safe(r.employee)}</td>

              <td className="border px-2 text-center">
                {safe(r.employee_name)}
              </td>

              <td className="border px-2 text-center">{safe(r.state_name)}</td>

              <td className="border px-2 text-center">{safe(r.substate)}</td>

              <td className="border px-2 text-center">
                {safe(r.retailer_type)}
              </td>

              <td className="border px-2 text-center">{safe(r.retailer_id)}</td>

              <td className="border px-2">{safe(r.retailer_name)}</td>

              <td className="border px-2 text-center">
                {safe(r.retailer_city)}
              </td>

              <td className="border px-2 text-center">
                {safe(r.retailer_area)}
              </td>

              <td className="border px-2 text-center">
                {safe(r.retailer_route)}
              </td>

              <td className="border px-2 text-center">
                {safe(r.retailer_mobile || r.retailer_phone)}
              </td>

              <td className="border px-2 text-center">
                {formatDate(r.retailer_created_at)}
              </td>

              <td className="border px-2 text-center">
                {safe(r.stockist_code)}
              </td>

              <td className="border px-2">{safe(r.stockist_name)}</td>

              <td className="border px-2 text-center font-semibold">
                {Number(r.total_gift || 0)}
              </td>

              {/* CLICKABLE POB */}
              <td
                className="border px-2 text-right font-semibold text-blue-600 cursor-pointer hover:underline"
                onClick={() => onPobClick(r)}
              >
                {Number(r.total_pob || 0).toLocaleString()}
              </td>
            </tr>
          ))}
        </tbody>

        <tfoot>
          <tr className="bg-blue-700 text-white font-bold">
            <td colSpan={15} className="border px-2 py-2 text-right">
              TOTAL
            </td>

            {/* DD Count Total */}
            <td className="border px-2 py-2 text-center">
              {rows.reduce((sum, r) => sum + Number(r.total_gift || 0), 0)}
            </td>

            {/* POB Total */}
            <td className="border px-2 py-2 text-right">
              {rows
                .reduce((sum, r) => sum + Number(r.total_pob || 0), 0)
                .toLocaleString()}
            </td>
          </tr>
        </tfoot>
      </table>

    </div>
  );
}

/* =========================
   REUSABLE
========================= */
const Select = ({ label, value, onChange, children }) => (
  <div className="grid grid-cols-[140px_1fr] items-center gap-2">
    <label>{label}</label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="border px-2 py-1"
    >
      {children}
    </select>
  </div>
);

